import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';

interface UseFirstTimeUserGuideResult {
  shouldShow: boolean;
  isLoading: boolean;
  dismiss: () => Promise<void>;
  error: string | null;
}

/**
 * Hook to manage first-time user guide overlay state
 * Checks if user has completed the guide and provides dismiss functionality
 */
export function useFirstTimeUserGuide(): UseFirstTimeUserGuideResult {
  const { user } = useAuth();
  const [shouldShow, setShouldShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    const checkGuideStatus = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('first_time_guide_completed')
          .eq('user_id', user.id)
          .maybeSingle();

        if (fetchError) {
          console.error('[useFirstTimeUserGuide] Error fetching guide status:', fetchError);
          setError(fetchError.message);
          setShouldShow(false); // Fail gracefully - don't show guide if we can't check
          return;
        }

        // Show guide if field is false or null (new users)
        setShouldShow(!data?.first_time_guide_completed);
      } catch (err) {
        console.error('[useFirstTimeUserGuide] Unexpected error:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setShouldShow(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkGuideStatus();
  }, [user?.id]);

  const dismiss = async () => {
    if (!user?.id) {
      console.warn('[useFirstTimeUserGuide] Cannot dismiss: no user');
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_time_guide_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateError) {
        console.error('[useFirstTimeUserGuide] Error updating guide status:', updateError);
        setError(updateError.message);
        return;
      }

      // Update local state immediately for responsive UI
      setShouldShow(false);
    } catch (err) {
      console.error('[useFirstTimeUserGuide] Unexpected error dismissing:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  return { shouldShow, isLoading, dismiss, error };
}
